import "./i18n";
import "./main.css";
// Self-hosted fonts as plain JS side-effect imports (NOT a CSS @import in main.css) —
// Lightning CSS (Tailwind v4's processor) doesn't rebase relative url() paths inside
// @fontsource's nested per-subset CSS when pulled through a Tailwind @import chain,
// causing silent 404s on font files in prod. Mirrors velaro-messaging's main.tsx.
import "@fontsource/rubik/300.css";
import "@fontsource/rubik/400.css";
import "@fontsource/rubik/500.css";
import "@fontsource/rubik/600.css";
import "@fontsource/rubik/700.css";
import "@fontsource/rubik/800.css";
import "@fontsource/rubik/900.css";
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/800.css";
// Import as raw string to bypass PostCSS: velaro-component-lib ships Tailwind v4
// CSS (@layer base) which Tailwind v3.4+ cannot process on Windows.
import compLibCss from "@velaro/velaro-component-lib/dist/velaro-component-lib.css?raw";
const _compLibStyle = document.createElement("style");
_compLibStyle.dataset.source = "velaro-component-lib";
_compLibStyle.textContent = compLibCss;
document.head.appendChild(_compLibStyle);

import React from "react";
import ReactDOM from "react-dom";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { createRoot } from "react-dom/client";
import { reportErrorToSupport } from "./components/GlobalErrorBoundary";
import { CircuitOpenError } from "./services/RestService";
import { loadClientConfig } from "./services/clientConfig";
import { applyDesignV2ToDocument } from "./services/designV2";
import { checkForStaleBundle } from "./utils/staleBundleGuard";

// Design System V2 flag — must be stamped onto <html> before first paint so
// the CSS in main.css's `[data-theme-v2="on"]` block applies without a
// flash of the old styling. Defaults to "on"; flip via localStorage or the
// Velaro Admin → Design System (2026) toggle page for an instant, no-deploy
// revert to the pre-refresh look.
applyDesignV2ToDocument();

// Catch JS errors that happen outside the React render tree (event handlers,
// setTimeout callbacks, etc.)
window.onerror = (message, source, lineno, colno, error) => {
  reportErrorToSupport({
    message: String(message),
    stack: error?.stack ?? `${source}:${lineno}:${colno}`,
    componentStack: "",
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    type: "async"
  });
  // Return false so the default browser error handling still runs
  return false;
};

// Catch unhandled Promise rejections (e.g. await inside useEffect with no catch)
window.addEventListener("unhandledrejection", (event) => {
  // Circuit-open errors are intentional fast-fails, not crashes — suppress them
  if (event.reason instanceof CircuitOpenError) return;

  const error =
    event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));
  reportErrorToSupport({
    message: error.message ?? "Unhandled promise rejection",
    stack: error.stack ?? "",
    componentStack: "",
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    type: "unhandled-rejection"
  });
});

// Named so links back from the messaging app (target="velaro-admin") land in
// this exact tab instead of spawning a new one every time.
window.name = "velaro-admin";

// MSAL's ssoSilent()/acquireTokenSilent() renews tokens via a hidden, invisible
// same-origin iframe that loads this exact SPA at /auth/callback (MsalConfig.ts
// redirectUri). In @azure/msal-browser v5 the iframe's OWN page must actively
// broadcast the auth response back to the parent via BroadcastChannel — the
// parent's SilentIframeClient just waits on that channel (default 10s timeout)
// and never reads the iframe's URL directly the way older MSAL versions did.
// Before this guard, this file instead booted the ENTIRE app inside that hidden
// iframe on every silent renewal: full config fetch, full React tree, and its
// own independent MSAL PublicClientApplication instance with no knowledge of
// the parent's in-flight interaction. That second instance never broadcast
// anything (so ssoSilent always timed out — "ssoSilent failed after
// no_accounts: timed_out"), while MsalConfig.ts's getMsalInstance() ran
// purgeStaleMsalState() on init and, on a stale velaro.msalLoginStartedAt stamp,
// wiped every msal.* key from localStorage/sessionStorage/cookies out from
// under the PARENT's real session mid-renewal — the concrete cause of "the
// refresh isn't clearing shit... tokens/jwt local storage really bad now."
// Fix: when framed at exactly this path (only ever true for MSAL's own hidden
// iframe — every other route enforces X-Frame-Options: DENY, so no legitimate
// embed reaches here), skip the full app entirely and instead call MSAL's own
// @azure/msal-browser/redirect-bridge entry point, whose sole job is reading
// the response off THIS frame's URL and broadcasting it to the parent — no
// PublicClientApplication instance, no config fetch, no purge. See
// docs/architecture/postmortems.md — admin-green login incident, 2026-07-25/26.
const isMsalSilentIframe =
  window.self !== window.top && window.location.pathname === "/auth/callback";

if (isMsalSilentIframe) {
  import("@azure/msal-browser/redirect-bridge")
    .then(({ broadcastResponseToMainFrame }) => broadcastResponseToMainFrame())
    .catch((ex) =>
      console.warn("[Auth] MSAL redirect-bridge failed:", ex?.message ?? ex)
    );
} else {
  // Fire-and-forget, never blocks first paint. Confirms the shell we just
  // loaded actually matches what's live and self-heals with one reload if not
  // -- see staleBundleGuard.ts. Never run inside the MSAL silent iframe branch
  // above; that frame is invisible and reloading it would just break renewal.
  checkForStaleBundle();

  const rootElement = document.getElementById("root");
  const root = createRoot(rootElement!);

  loadClientConfig().then(() => {
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
    reportWebVitals();
  });
}
